import React, { useMemo, useState } from 'react';
import { Lock, Unlock, ChevronDown, ChevronRight } from 'lucide-react';
import { Badge, Button, Callout } from '../../components/ui';
import { Form, JsonFormField } from '../../components/form';
import { zJsonObject, zWorkflowStepInput } from '../../generated/api/zod.gen.js';
import { useBuilderForm } from './useBuilderForm.js';
import type { CalloutTone } from '../../components/ui';
import type { WorkflowBuilderStep } from './WorkflowBuilderTypes.js';
import type { WorkflowContract } from '../../generated/api/types.gen.js';

interface StepContractTabProps {
  step: WorkflowBuilderStep;
  steps: WorkflowBuilderStep[];
  onUpdateStep: (stepId: string, updates: Partial<WorkflowBuilderStep>) => void;
}

const labelClass = 'block text-xs font-medium text-muted mb-1';

type NegotiationStatus = 'pending' | 'negotiated' | 'overridden' | 'error';

const STATUS_CONFIG: Record<NegotiationStatus, { label: string; tone: CalloutTone }> = {
  pending: { label: 'Pending', tone: 'accent' },
  negotiated: { label: 'Auto-negotiated', tone: 'success' },
  overridden: { label: 'Manually overridden', tone: 'warning' },
  error: { label: 'Error', tone: 'danger' },
};

const StatusBanner: React.FC<{ status: NegotiationStatus }> = ({ status }) => {
  const cfg = STATUS_CONFIG[status];
  return <Callout tone={cfg.tone}>{cfg.label}</Callout>;
};

function schemaFrom(contract: WorkflowContract | undefined | null): Record<string, unknown> | null {
  const schema = contract?.jsonSchema ?? contract?.schema;
  return schema && typeof schema === 'object' && !Array.isArray(schema) ? schema : null;
}

function propertyNames(contract: WorkflowContract | undefined | null): string[] {
  const schema = schemaFrom(contract);
  const properties = schema?.properties;
  return properties && typeof properties === 'object' && !Array.isArray(properties) ? Object.keys(properties) : [];
}

function analyzeEdge(
  source: WorkflowBuilderStep,
  targetInput: WorkflowContract | undefined | null,
): { status: 'compatible' | 'mismatch' | 'unknown'; missingFields: string[] } {
  const sourceFields = propertyNames(source.outputContract as WorkflowContract | null);
  const targetFields = propertyNames(targetInput);
  if (sourceFields.length === 0 || targetFields.length === 0) return { status: 'unknown', missingFields: [] };
  const missingFields = targetFields.filter((field) => !sourceFields.includes(field));
  return missingFields.length === 0 ? { status: 'compatible', missingFields } : { status: 'mismatch', missingFields };
}

function edgeStatusVariant(status: 'compatible' | 'mismatch' | 'unknown'): 'success' | 'warning' | 'neutral' {
  if (status === 'compatible') return 'success';
  if (status === 'mismatch') return 'warning';
  return 'neutral';
}

/** A contract's schema as an editable JSON object: the generated contract field, narrowed to an object. */
const schemaEditSchema = zWorkflowStepInput.pick({ outputContract: true }).extend({ outputContract: zJsonObject });

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const SchemaEditor: React.FC<{
  title?: string;
  schema: Record<string, unknown> | null;
  onUpdate?: (contract: WorkflowContract | null) => void;
}> = ({ title, schema, onUpdate }) => {
  const form = useBuilderForm(
    schemaEditSchema,
    { outputContract: schema ?? {} },
    (_field, all) => {
      // Only a valid schema object reaches the step; text that is not one stays in the editor with its message.
      if (isRecord(all.outputContract)) onUpdate?.({ jsonSchema: all.outputContract, strict: true });
    },
    { mode: 'onChange', sync: false },
  );
  return (
    <div className="mt-2">
      {title && <div className="text-xs font-medium text-body mb-1">{title}</div>}
      <Form form={form} onSubmit={() => undefined} className="gap-0">
        <JsonFormField name="outputContract" label="Schema (JSON)" rows={10} />
      </Form>
    </div>
  );
};

const JsonSchemaPanel: React.FC<{
  title?: string;
  contract: WorkflowContract | undefined | null;
  editable: boolean;
  onUpdate?: (contract: WorkflowContract | null) => void;
}> = ({ title, contract, editable, onUpdate }) => {
  const schema = schemaFrom(contract);
  const names = propertyNames(contract);

  if (!schema && !editable) {
    return (
      <div className="px-3 py-2 text-xs text-faint text-center border border-dashed rounded-sm">No schema defined</div>
    );
  }

  if (editable) return <SchemaEditor title={title} schema={schema} onUpdate={onUpdate} />;

  return (
    <div className="border rounded-sm overflow-hidden mt-2">
      {title && <div className="px-3 py-2 text-xs font-medium text-body border-b">{title}</div>}
      <div className="px-3 py-2">
        <div className="flex flex-wrap gap-1.5">
          {names.length > 0 ? (
            names.map((name) => (
              <span key={name} className="text-[10px] px-1.5 py-0.5 rounded-sm bg-surface-alt text-muted font-mono">
                {name}
              </span>
            ))
          ) : (
            <span className="text-xs text-faint">No top-level properties</span>
          )}
        </div>
        <pre className="mt-2 max-h-64 overflow-auto rounded-sm bg-surface-alt p-2 text-[11px] text-muted">
          {JSON.stringify(schema, null, 2)}
        </pre>
      </div>
    </div>
  );
};

const UpstreamSection: React.FC<{
  step: WorkflowBuilderStep;
  contract: WorkflowContract | undefined | null;
  editable: boolean;
  onUpdate?: (contract: WorkflowContract | null) => void;
}> = ({ step, contract, editable, onUpdate }) => {
  const [expanded, setExpanded] = useState(true);
  const names = propertyNames(contract);
  const edge = analyzeEdge(step, contract);
  return (
    <div className="border rounded-sm mb-2">
      <Button
        variant="ghost"
        className="w-full justify-start rounded-none px-3 py-2 text-left"
        aria-expanded={expanded}
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <span className="text-body font-medium">{step.name || 'Unnamed step'}</span>
        <Badge variant={edgeStatusVariant(edge.status)} className="ml-auto">
          {edge.status === 'compatible' ? 'Compatible' : edge.status === 'mismatch' ? 'Needs mapping' : 'Unvalidated'}
        </Badge>
      </Button>
      {expanded && (
        <div className="px-3 pb-2">
          <div className="mb-2 text-xs text-faint">
            {edge.status === 'mismatch'
              ? `Missing from ${step.name}: ${edge.missingFields.join(', ')}`
              : names.length
                ? `Input fields: ${names.join(', ')}`
                : 'No input schema to compare yet'}
          </div>
          <JsonSchemaPanel contract={contract} editable={editable} onUpdate={onUpdate} />
        </div>
      )}
    </div>
  );
};

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex items-center justify-center py-6 text-sm text-faint border border-dashed rounded-sm mt-2">
    {message}
  </div>
);

export const StepContractTab: React.FC<StepContractTabProps> = ({ step, steps, onUpdateStep }) => {
  const outputContract = step.outputContract as WorkflowContract | null;
  const inputContracts = step.inputContracts as Record<string, WorkflowContract> | null;
  const [editMode, setEditMode] = useState(false);
  const upstreamSteps = useMemo(() => steps.filter((s) => s.order < step.order && s.id !== step.id), [step, steps]);
  const status: NegotiationStatus = step.contractNegotiationStatus ?? 'pending';

  const updateContracts = (patch: {
    outputContract?: WorkflowContract | null;
    inputContracts?: Record<string, WorkflowContract>;
    contractNegotiationStatus?: NegotiationStatus;
  }) => {
    onUpdateStep(step.id.toString(), {
      ...(patch.outputContract !== undefined ? { outputContract: patch.outputContract } : {}),
      ...(patch.inputContracts !== undefined ? { inputContracts: patch.inputContracts } : {}),
      contractVersion: (step.contractVersion ?? 0) + 1,
      contractNegotiationStatus: patch.contractNegotiationStatus ?? 'overridden',
    });
  };

  return (
    <div className="flex flex-col gap-5">
      <StatusBanner status={status} />

      <section>
        <label className={labelClass}>This step produces</label>
        {outputContract || editMode ? (
          <JsonSchemaPanel
            contract={outputContract}
            editable={editMode}
            onUpdate={(contract) => updateContracts({ outputContract: contract })}
          />
        ) : (
          <EmptyState message="No output schema defined" />
        )}
      </section>

      <section>
        <label className={labelClass}>Receives from</label>
        {upstreamSteps.length > 0 ? (
          upstreamSteps.map((upstream) => (
            <UpstreamSection
              key={upstream.id}
              step={upstream}
              contract={inputContracts?.[upstream.name] ?? inputContracts?.[upstream.id.toString()]}
              editable={editMode}
              onUpdate={(contract) => {
                const updated = { ...(inputContracts ?? {}) };
                if (contract) updated[upstream.name] = contract;
                updateContracts({ inputContracts: updated });
              }}
            />
          ))
        ) : (
          <EmptyState message="No upstream steps - this is a root step" />
        )}
      </section>

      <div className="flex items-center gap-2">
        <Button variant="secondary" size="sm" onClick={() => setEditMode(!editMode)}>
          {editMode ? <Lock size={12} /> : <Unlock size={12} />}
          {editMode ? 'Lock' : 'Edit override'}
        </Button>
      </div>

      {step.contractNegotiationStatus === 'overridden' && (
        <p className="text-xs text-faint">Contract manually overridden (version {step.contractVersion ?? 1})</p>
      )}
    </div>
  );
};
